import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import adminAxios from '../../services/adminAxios';
import '../../assets/css/admin/profile.css';

const MyProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Password change states
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  
  // Profile image states
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Notification state
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Fetch profile data
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get('/api/admin/profile');
      
      if (response.data.success) {
        setProfile(response.data.admin);
        setImagePreview(response.data.admin.profile_image || null);
        setError(null);
      } else {
        setError('Failed to load profile data');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Error loading profile data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle editing mode
  const toggleEditMode = () => {
    if (!isEditing) {
      setEditFormData({
        name: profile?.name || '',
        email: profile?.email || '',
        phone: profile?.phone || '',
        bio: profile?.bio || '',
      });
    }
    setIsEditing(!isEditing);
  };

  // Handle input changes for profile edit
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle profile image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFormData(prev => ({
        ...prev,
        profile_image: file
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Submit updated profile
  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      
      Object.keys(editFormData).forEach(key => {
        if (key !== 'profile_image' || editFormData[key] !== null) {
          formData.append(key, editFormData[key]);
        }
      });

      const response = await adminAxios.put('/api/admin/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setProfile({
          ...profile,
          ...response.data.admin
        });
        setIsEditing(false);
        setNotification({ 
          show: true, 
          type: 'success', 
          message: 'Profile updated successfully!' 
        });
        setTimeout(() => {
          setNotification({ show: false, type: '', message: '' });
        }, 3000);
      } else {
        setNotification({ 
          show: true, 
          type: 'error', 
          message: response.data.message || 'Failed to update profile' 
        });
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setNotification({ 
        show: true, 
        type: 'error', 
        message: 'Error updating profile. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle password change mode
  const togglePasswordChange = () => {
    setIsChangingPassword(!isChangingPassword);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordErrors({});
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error on field change
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate password change
  const validatePasswordChange = () => {
    const errors = {};
    const { currentPassword, newPassword, confirmPassword } = passwordData;
    
    if (!currentPassword) errors.currentPassword = 'Current password is required';
    if (!newPassword) errors.newPassword = 'New password is required';
    else if (newPassword.length < 8) errors.newPassword = 'Password must be at least 8 characters';
    
    if (!confirmPassword) errors.confirmPassword = 'Please confirm your new password';
    else if (confirmPassword !== newPassword) errors.confirmPassword = 'Passwords do not match';
    
    return errors;
  };

  // Submit password change
  const handleSubmitPasswordChange = async (e) => {
    e.preventDefault();
    
    const errors = validatePasswordChange();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    setIsPasswordSubmitting(true);
    
    try {
      const response = await adminAxios.post('/api/admin/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (response.data.success) {
        setNotification({ 
          show: true, 
          type: 'success', 
          message: 'Password changed successfully!' 
        });
        setIsChangingPassword(false);
        setTimeout(() => {
          setNotification({ show: false, type: '', message: '' });
        }, 3000);
      } else {
        setPasswordErrors({
          currentPassword: response.data.message || 'Failed to change password'
        });
      }
    } catch (err) {
      console.error('Error changing password:', err);
      if (err.response && err.response.data && err.response.data.field) {
        setPasswordErrors({
          [err.response.data.field]: err.response.data.message
        });
      } else {
        setNotification({ 
          show: true, 
          type: 'error', 
          message: 'Error changing password. Please try again.' 
        });
      }
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading-container">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="alert alert-danger">{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-profile-page">
        {notification.show && (
          <div className={`alert alert-${notification.type === 'success' ? 'success' : 'danger'} notification-alert`}>
            {notification.message}
          </div>
        )}

        <div className="profile-header">
          <h2 className="page-title">My Profile</h2>
          <p className="text-muted">Manage your account settings and profile information</p>
        </div>

        <div className="profile-content">
          <div className="profile-card">
            <div className="card-header">
              <h3>Profile Information</h3>
              {!isEditing && (
                <button 
                  className="btn btn-outline-primary" 
                  onClick={toggleEditMode}
                >
                  <i className="fa fa-pencil me-2"></i> Edit Profile
                </button>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmitUpdate} className="profile-edit-form">
                <div className="profile-image-section">
                  <div className="profile-image-container">
                    <div 
                      className="profile-image-upload"
                      onClick={triggerFileInput}
                    >
                      {imagePreview ? (
                        <div className="profile-image">
                          <img src={imagePreview} alt="Profile" />
                          <div className="image-overlay">
                            <i className="fa fa-camera"></i>
                          </div>
                        </div>
                      ) : (
                        <div className="profile-image-placeholder">
                          <i className="fa fa-user"></i>
                          <span>Add Image</span>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        accept="image/*"
                        hidden
                      />
                    </div>
                  </div>
                  <div className="image-help-text">
                    Click on the image to change your profile picture
                  </div>
                </div>

                <div className="form-section">
                  <h4>Personal Information</h4>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">Full Name</label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={editFormData.name || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email Address</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          value={editFormData.email || ''}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="phone" className="form-label">Phone Number</label>
                        <input
                          type="text"
                          className="form-control"
                          id="phone"
                          name="phone"
                          value={editFormData.phone || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="bio" className="form-label">Bio</label>
                    <textarea
                      className="form-control"
                      id="bio"
                      name="bio"
                      rows="3"
                      value={editFormData.bio || ''}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={toggleEditMode}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="profile-details">
                <div className="profile-image-section">
                  {profile.profile_image ? (
                    <div className="profile-image">
                      <img src={profile.profile_image} alt={profile.name} />
                    </div>
                  ) : (
                    <div className="profile-image-placeholder">
                      <i className="fa fa-user"></i>
                    </div>
                  )}
                </div>
                
                <div className="info-section">
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{profile.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Email</div>
                    <div className="info-value">{profile.email}</div>
                  </div>
                  {profile.phone && (
                    <div className="info-row">
                      <div className="info-label">Phone</div>
                      <div className="info-value">{profile.phone}</div>
                    </div>
                  )}
                  {profile.bio && (
                    <div className="info-row">
                      <div className="info-label">Bio</div>
                      <div className="info-value bio-text">{profile.bio}</div>
                    </div>
                  )}
                  <div className="info-row">
                    <div className="info-label">Role</div>
                    <div className="info-value">
                      <span className="role-badge">Administrator</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="profile-card security-card">
            <div className="card-header">
              <h3>Security</h3>
              {!isChangingPassword && (
                <button 
                  className="btn btn-outline-primary" 
                  onClick={togglePasswordChange}
                >
                  <i className="fa fa-lock me-2"></i> Change Password
                </button>
              )}
            </div>

            {isChangingPassword ? (
              <form onSubmit={handleSubmitPasswordChange} className="password-change-form">
                <div className="form-section">
                  <div className="mb-3">
                    <label htmlFor="currentPassword" className="form-label">Current Password</label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.currentPassword ? 'is-invalid' : ''}`}
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                    {passwordErrors.currentPassword && (
                      <div className="invalid-feedback">{passwordErrors.currentPassword}</div>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">New Password</label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.newPassword ? 'is-invalid' : ''}`}
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                    {passwordErrors.newPassword && (
                      <div className="invalid-feedback">{passwordErrors.newPassword}</div>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.confirmPassword ? 'is-invalid' : ''}`}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                    {passwordErrors.confirmPassword && (
                      <div className="invalid-feedback">{passwordErrors.confirmPassword}</div>
                    )}
                  </div>
                </div>

                <div className="password-requirements">
                  <h5>Password Requirements:</h5>
                  <ul>
                    <li>At least 8 characters long</li>
                    <li>Include at least one uppercase letter</li>
                    <li>Include at least one number</li>
                    <li>Include at least one special character</li>
                  </ul>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={togglePasswordChange}
                    disabled={isPasswordSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isPasswordSubmitting}
                  >
                    {isPasswordSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="security-info">
                <div className="info-row">
                  <div className="info-label">Password</div>
                  <div className="info-value">••••••••</div>
                </div>
                <div className="info-row">
                  <div className="info-label">Last Password Change</div>
                  <div className="info-value">
                    {profile.last_password_change ? 
                      new Date(profile.last_password_change).toLocaleDateString() :
                      'Never'
                    }
                  </div>
                </div>
                <div className="security-tips">
                  <h5>Security Tips</h5>
                  <ul>
                    <li>Use a strong, unique password for your admin account</li>
                    <li>Change your password regularly</li>
                    <li>Never share your login credentials with others</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MyProfilePage;
